import React from "react";

import {
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
} from "@/components/Broker/brokerAlertUi";
import {
  CUSTOM_CSV_BROKER_SELECT_TRIGGER_CLASS,
  CUSTOM_CSV_SELECT_CONTENT_PROPS,
} from "../customCsvWizardUi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  CUSTOM_CSV_UNMAP_COLUMN_VALUE,
  getSourceValueForTarget,
  sanitizeCsvCellDisplay,
  setSourceValueForTarget,
  sourceOptionsForTarget,
} from "../../../../utils/customCsvImport";

export type CsvValueMappingLayout = "source-first" | "target-first";

export interface CsvValueMappingTableProps {
  columnName: string;
  distinctValues: string[];
  targets: readonly string[];
  mapping: Record<string, string>;
  onChange: (mapping: Record<string, string>) => void;
  ignoredValues?: string[];
  /** source-first: one row per file value (many values can map to the same target). */
  layout?: CsvValueMappingLayout;
}

const TABLE_HEAD_CLASS =
  "font-[family-name:var(--font-primary)] text-xs font-normal normal-case tracking-normal text-[color:var(--brand-tropical-surf)]";

const TABLE_CELL_VALUE_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500";

const TABLE_CELL_TARGET_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-gray-700";

function ValueMappingSelect({
  id,
  value,
  placeholder,
  options,
  optionLabels,
  onValueChange,
}: {
  id: string;
  value: string;
  placeholder: string;
  options: string[];
  optionLabels?: Record<string, string>;
  onValueChange: (value: string) => void;
}): React.ReactElement {
  return (
    <Select
      value={value || undefined}
      onValueChange={onValueChange}
    >
      <SelectTrigger id={id} className={CUSTOM_CSV_BROKER_SELECT_TRIGGER_CLASS}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent {...CUSTOM_CSV_SELECT_CONTENT_PROPS}>
        {options.map((option) => (
          <SelectItem key={option} value={option}>
            {optionLabels?.[option] ?? sanitizeCsvCellDisplay(option)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const CsvValueMappingTable = ({
  columnName,
  distinctValues,
  targets,
  mapping,
  onChange,
  ignoredValues = [],
  layout = "source-first",
}: CsvValueMappingTableProps): React.ReactElement => {
  if (layout === "target-first") {
    return (
      <div className="flex flex-col gap-2">
        <div
          data-shadcn-data-table
          data-custom-csv-value-mapping-table=""
          className="w-full overflow-hidden rounded-[6px] border border-border bg-background"
        >
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className={TABLE_HEAD_CLASS}>
                  Pick from your file ({columnName})
                </TableHead>
                <TableHead className={TABLE_HEAD_CLASS}>Tessera means</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {targets.map((target) => {
                const selectedSource = getSourceValueForTarget(mapping, target);
                const options = sourceOptionsForTarget(distinctValues, mapping, target);
                const selectId = `custom-csv-value-map-${columnName}-${target}`;

                return (
                  <TableRow key={target}>
                    <TableCell className="align-middle">
                      <ValueMappingSelect
                        id={selectId}
                        value={selectedSource}
                        placeholder="Select…"
                        options={options}
                        onValueChange={(next) =>
                          onChange(setSourceValueForTarget(mapping, target, next))
                        }
                      />
                    </TableCell>
                    <TableCell className={cn(TABLE_CELL_TARGET_CLASS, "whitespace-nowrap align-middle")}>
                      {target}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {ignoredValues.length > 0 ? (
          <p className={cn(BROKER_ALERT_DESC_CLASS, "m-0")}>
            Not mapped (skipped automatically):{" "}
            {ignoredValues.map((value) => sanitizeCsvCellDisplay(value)).join(", ")}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div
        data-shadcn-data-table
        data-custom-csv-value-mapping-table=""
        className="w-full overflow-hidden rounded-[6px] border border-border bg-background"
      >
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className={TABLE_HEAD_CLASS}>
                Your file ({columnName})
              </TableHead>
              <TableHead className={TABLE_HEAD_CLASS}>Tessera means</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {distinctValues.map((value) => {
              const selectId = `custom-csv-value-map-${columnName}-${value}`;
              const mapped = mapping[value] ?? "";
              return (
                <TableRow key={value}>
                  <TableCell className={cn(TABLE_CELL_VALUE_CLASS, "align-middle")}>
                    {sanitizeCsvCellDisplay(value)}
                  </TableCell>
                  <TableCell className="align-middle">
                    <ValueMappingSelect
                      id={selectId}
                      value={mapped}
                      placeholder="Select…"
                      options={[...targets, CUSTOM_CSV_UNMAP_COLUMN_VALUE]}
                      optionLabels={{
                        [CUSTOM_CSV_UNMAP_COLUMN_VALUE]: "Skip (not a trade)",
                      }}
                      onValueChange={(next) => {
                        if (next === CUSTOM_CSV_UNMAP_COLUMN_VALUE) {
                          const { [value]: _removed, ...rest } = mapping;
                          onChange(rest);
                          return;
                        }
                        onChange({ ...mapping, [value]: next });
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {ignoredValues.length > 0 ? (
        <p className={cn(BROKER_ALERT_DESC_CLASS, "m-0")}>
          Not mapped (skipped automatically):{" "}
          {ignoredValues.map((value) => sanitizeCsvCellDisplay(value)).join(", ")}
        </p>
      ) : null}
    </div>
  );
};

export default CsvValueMappingTable;
